import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import parse from 'html-react-parser';

import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';

import { snapCenterToCursor } from '@dnd-kit/modifiers';

import '../../../css/Cloze.scss';

import { ContentType, LessonMode } from '../../../Constants';

import dragDropService from '../../services/DragDropService';

import playerService from '../../services/PlayerService';
import promptService from '../../services/PromptService';
import questionService from '../../services/QuestionService';
import styleService from '../../services/StyleService';
import textQuestionUtilsService from '../../services/TextQuestionUtilsService';
import utilsService from '../../services/UtilsService';
import toolbarService from '../../services/ToolbarService';

import useForceUpdate from '../../../hooks/useForceUpdate';
import useWirisParser from '../../../hooks/useWirisParser';

import Guideline from '../tools/Guideline';

import DndKitDroppable from '../DndKitDroppable';
import DndKitDraggable from '../DndKitDraggable';
import DndKitOverlay from '../DndKitOverlay';

import FeedbackIcon from '../FeedbackIcon';
import HtmlComponent from '../HtmlComponent';
import OptionalImage from '../OptionalImage';
import useStyleEvents from '../../../hooks/useStyleEvents';
import responseService from '../../services/ResponseService';
import navMenuService from '../../services/NavMenuService';
import PrintQuestionNumber from './PrintQuestionNumber';
import { register } from '../../../i18n';

const t2 = register('GlobalQuestionLabels');

const ClozeDragDrop = observer(({ lessonElementId }) => {
  const {
    lessonManager,
    questionFeedbackManager,
    toolbarManager
  } = useContext(MobXProviderContext);

  const wirisRef = useRef();
  useWirisParser(wirisRef);

  const forceUpdate = useForceUpdate();

  const [loadingDragDrop, setLoadingDragDrop] = useState(true);

  const [allowRenderDragDropAnswerBankSection, setAllowRenderDragDropAnswerBankSection] = useState(false);

  const [responseItemAnswerBankOptions, setResponseItemAnswerBankOptions] = useState([]);

  const [dragStartDndKitEvent, setDragStartDndKitEvent] = useState(null);

  const [dragEndResult, setDragEndResult] = useState(null);

  const [blankIndexMap, setBlankIndexMap] = useState(new Map());

  const { getStyleVar } = styleService;

  const { stripTagsAndEntities, stripWrappingParagraphTags } = utilsService;

  const {
    lessonElementState,
    model,
    userInputMap,
  } = questionService.initQuestionComponent({
    lessonElementId,
    questionClassName: 'cloze-question'
  });

  const sensors = useSensors(
    useSensor(KeyboardSensor),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 0.1,
        tolerance: 5
      }
    })
  );

  const initResponseItemAnswerBankOptions = () => {
    dragDropService.initResponseItemAnswerBankOptions({
      model,
      setResponseItemAnswerBankOptions
    });
  };

  useStyleEvents(lessonElementId);

  /** equivalent to componentDidMount(), i.e. only called on initial load */
  useEffect(() => {
    dragDropService.handleDragDropInitUseEffect({
      initResponseItemAnswerBankOptions,
      setLoadingDragDrop
    });

    setTimeout(() => {
      setAllowRenderDragDropAnswerBankSection(true);
    }, 250);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * @param {import('@dnd-kit/core').DragEndEvent} dndKitEvent
   */
  const onDragEnd = (dndKitEvent) => {
    dragDropService.handleDragEnd({
      dndKitEvent,
      forceUpdate,
      lessonElementState,
      lessonElementId,
      model,
      result: undefined,
      setDragEndResult
    });
  };

  /** equivalent to componentWillUnmount(), i.e. called whenever component is unmounting */
  useEffect(() => () => {
    return dragDropService.handleDragDropUnmountUseEffect(ContentType.CLOZE_DRAG_DROP);
  });

  // Announce feedback changes to screen readers
  useEffect(() => {
    const feedbackState = questionFeedbackManager.getUiState(lessonElementId);
    const showAnswerFeedback = feedbackState?.showAnswerFeedback;

    if (showAnswerFeedback && model?.validation?.prompts) {
      const correctCount = model.validation.prompts.filter((prompt) => responseService.isAnswerCorrect(lessonElementId, prompt.id, { lessonElementState, model })
      ).length;
      const totalCount = model.validation.prompts.length;
      const incorrectCount = totalCount - correctCount;

      const announcement = `Feedback shown: ${correctCount} correct, ${incorrectCount} incorrect out of ${totalCount} answers`;
      const liveRegion = document.getElementById(`${lessonElementId}-cloze-dragdrop-feedback-announcement`);
      if (liveRegion) {
        liveRegion.textContent = announcement;
      }
    }
  }, [lessonElementId, lessonElementState, model, questionFeedbackManager]);

  const getAbsoluteDragDropResponseItemStyles = (responseItem) => {
    return dragDropService.getAbsoluteDragDropResponseItemStyles({
      model,
      responseItem
    });
  };

  const navToCurrentLessonElement = async () => {
    if (lessonManager.currentLessonElementId !== lessonElementId) {
      responseService.setSolutionDialogOpen(false);
      responseService.setHintDialogOpen(false);
      await navMenuService.navToSlideClickHandler(lessonElementId, { shouldScrollQuestionIntoView: false });
    }
  };

  const getBlankName = (dataId) => {
    // Use the blank index if available, otherwise fall back to the ID
    const blankIndex = blankIndexMap.get(dataId);
    if (blankIndex !== undefined) {
      return `blank ${blankIndex}`;
    }
    // Fallback: try to extract number from ID
    const blankMatch = dataId.match(/blank(\d+)/i);
    if (blankMatch) {
      return `blank ${blankMatch[1]}`;
    }
    return `blank ${dataId}`;
  };

  const getAnswerText = (draggableId) => {
    // Extract answer text from the draggable data
    const draggableData = dragStartDndKitEvent?.active?.data?.current;
    if (draggableData?.draggableValue) {
      const { text, isImage } = utilsService.getAccessibleTextFromHtml(draggableData.draggableValue);
      if (isImage) {
        return text || 'image answer';
      }
      return text || draggableData.draggableValue;
    }
    return `answer ${draggableId}`;
  };

  const renderClozeDragDrop = (_props = null) => {
    const isCustomAnswerBankStyling = model?.answerLayoutType === 'manual';
    const answerBankPositionClassName = `answer-bank-position-${ model?.answerBankPosition || 'bottom'}`;
    const clozeQuestionInnerClassNames = classNames('cloze-question-inner', answerBankPositionClassName);
    const questionTitle = <HtmlComponent htmlStr={model.questionText || ''} />;

    let answerBankBackgroundUrl = '';
    if (model.customAnswerLayoutBackground && model.customAnswerLayoutBackgroundHtml) {
      parse(model.customAnswerLayoutBackgroundHtml, {
        replace: (domNode) => {
          if (domNode.attribs?.src) {
            answerBankBackgroundUrl = domNode.attribs.src;
          }
        }
      });
    }
    return (
      <div ref={wirisRef} className='cloze-question'>
        <div className='cloze-question-section-title'>
          {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
          <div className='test-item-question'>
            {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
            {questionTitle}
          </div>
          <OptionalImage model={model} runtime={true} />
        </div>
        <DndContext
          accessibility={{
            announcements: {
              onDragStart({ active }) {
                const answerText = getAnswerText(active.id);
                return `Picked up ${answerText}`;
              },
              onDragOver({ active, over }) {
                const answerText = getAnswerText(active.id);
                if (over) {
                  const blankName = getBlankName(over.id);
                  return `Moving ${answerText} to ${blankName}`;
                }
                return `Moving ${answerText}`;
              },
              onDragEnd({ active, over }) {
                const answerText = getAnswerText(active.id);
                if (over) {
                  const blankName = getBlankName(over.id);
                  return `Dropped ${answerText} in ${blankName}`;
                }
                return `Dropped ${answerText}`;
              },
            },
            restoreFocus: true,
          }}
          modifiers={[snapCenterToCursor]}
          onDragEnd={(dndKitEvent) => {
            setDragStartDndKitEvent(null);
            onDragEnd(dndKitEvent);
          }}
          onDragStart={(dndKitEvent) => {
            navToCurrentLessonElement();
            setDragEndResult(null);
            setDragStartDndKitEvent(dndKitEvent);
          }}
          sensors={sensors}>
          <div aria-live='polite' className='sr-only'>
            Use Tab to navigate between draggable answers and blank spaces. Press Space or Enter to start dragging, then use arrow keys to move and Space or Enter to drop.
          </div>
          {/* Live region for feedback announcements */}
          <div
            aria-atomic='true'
            aria-live='polite'
            className='sr-only'
            id={`${lessonElementId}-cloze-dragdrop-feedback-announcement`} />
          <div className={clozeQuestionInnerClassNames}>
            <div className='cloze-question-section-body' onClick={toolbarService.handleContentResourceClick}
              onKeyDown={(e) => {
                if (e?.key === 'Enter') {
                  toolbarService.handleContentResourceClick(e);
                }
              }}>
              <div className='test-item-answers'>
                <div className='cloze-body cloze-drag-drop-body'>
                  <div className='cloze-body-nodes'>
                    {lessonElementState?.questionBehavior?.renderClozeBody({
                      lessonElementId, model, renderBlankSection
                    })}
                  </div>
                </div>
              </div>
            </div>

            {allowRenderDragDropAnswerBankSection && dragDropService.renderDragDropAnswerBankSection_dndKit({
              CONTENT_TYPE_OBJ: ContentType.CLOZE_DRAG_DROP,
              answerBankBackgroundUrl,
              dragEndResult,
              dragStartDndKitEvent,
              getAbsoluteDragDropResponseItemStyles,
              isCustomAnswerBankStyling,
              lessonElementId,
              lessonElementState,
              model,
              responseItemAnswerBankOptions
            })}
          </div>
          {createPortal(
            <DragOverlay className='overlay-cloze' dropAnimation={null} wrapperElement='span'>
              {dragStartDndKitEvent?.active?.data?.current?.draggableId ? (
                <DndKitOverlay
                  dragStartDndKitEvent={dragStartDndKitEvent}
                  model={model} />
              ) : null}
            </DragOverlay>, document.getElementById('the-body'))}

        </DndContext>
      </div>
    );
  };

  /** @param {import('html-react-parser').DOMNode} domNode */
  const renderBlankSection = (domNode, { lessonElementId } = {}) => {
    const dataId = domNode.attribs['data-id'];

    // Track the blank index for accessibility announcements
    if (!blankIndexMap.has(dataId)) {
      blankIndexMap.set(dataId, (blankIndexMap.size + 1));
      setBlankIndexMap(blankIndexMap);
    }

    const uiState = questionFeedbackManager.getUiState?.(lessonElementId);
    const { noSubmit } = questionFeedbackManager;

    const showAnswerFeedback = (uiState?.showAnswerFeedback && !noSubmit) || false;

    let nodeClassNames = domNode.attribs['class'];
    nodeClassNames = classNames(nodeClassNames, {
      correct: false, // we do not want students seeing this className in the dev tools
      feedback: showAnswerFeedback,
      incorrect: false // we do not want students seeing this className in the dev tools
    }, `${model.responseFormat || ''}`);

    const styleStr = domNode.attribs['style'] || '';
    const styleObj = styleService.convertStrToStyleObj(styleStr);

    const responseAnswerPrompt = promptService.getCurrentPrompt({
      dataId,
      lessonElementState,
      model,
      promptType: 'responseAnswerPrompt',
    }) || {};

    const { id, text, ...responseAnswerPromptRest } = responseAnswerPrompt;

    userInputMap?.set(dataId, {
      dataId,
      text: responseAnswerPrompt?.text?.trim?.()/* || clozeText */,
      ...responseAnswerPromptRest
    });

    const currentUserInput = lessonElementState?.currentResponseAnswer?.userInputMap?.get(dataId) || userInputMap?.get(dataId);
    const currentUserInputValue = currentUserInput?.text || '';
    const currentUserInputId = currentUserInput?.answerId || currentUserInput?.id || '';

    const responseItems = utilsService.getResponseItemsObjArrayFromStringArray([...model?.responseItems] || []);
    const currentResponseItemIndex = currentUserInputId ? responseItems?.findIndex((responseItem) => {
      return responseItem.id === currentUserInputId;
    }) || 0 : undefined;
    const currentResponseItem = currentUserInputId
      ? responseItems?.find((responseItem) => responseItem.id === currentUserInputId)
      : null;
    const blankDisplayHtml = model?.responseFormat === 'image' &&
      typeof (currentUserInput?.altText ?? currentResponseItem?.altText) !== 'undefined'
      ? utilsService.getImageResponseItemHtml({
        text: currentUserInputValue,
        altText: currentUserInput?.altText ?? currentResponseItem?.altText ?? ''
      })
      : currentUserInputValue;

    const { playerMode } = lessonManager;

    const clozeSectionInputWrapperClassNames = classNames('cloze-section-input-wrapper', nodeClassNames, playerMode, { 'list-number-wrapper': model.numberedBlanks });
    const isDragDisabled = playerService.isReadOnly(lessonElementId) || !currentUserInputId || false;

    const blankIndex = blankIndexMap.get(dataId);
    const blankNumber = blankIndex !== undefined ? blankIndex : 'unknown';
    const blankAccessibleText = (model?.responseFormat === 'image' &&
      (currentUserInput?.altText || currentResponseItem?.altText))
      ? (currentUserInput?.altText || currentResponseItem?.altText)
      : currentUserInputValue;
    const blankLabel = blankAccessibleText
      ? `Blank ${blankNumber}: ${stripTagsAndEntities(blankAccessibleText)}`
      : `Blank ${blankNumber}`;

    return (
      <div key={dataId} aria-label={blankLabel}
        className={classNames('cloze-blank-section cloze-section drag-drop-section', {
          // TODO remove // editorClassName
        }, nodeClassNames)}
        data-id={dataId}
        role='region'>
        <div className={clozeSectionInputWrapperClassNames}>
          {model.numberedBlanks && <div className='list-number' />}
          <DndKitDroppable droppableId={dataId} index={blankIndexMap.get(dataId)} tabIndex={0}>
            <div className='cloze-blank-droppable'/* tabIndex='0' */>
              <div className={classNames('cloze-blank-droppable-inner', {
                'cloze-blank-droppable-inner-has-value': !!currentUserInputValue/* && !droppableSnapshot.draggingFromThisWith */
              })} data-prompt-id={dataId} style={{
                // note: order is important here as styleObj needs to potentially override default width & height
                width: getStyleVar('--theme-drag-drop-blank-section-default-width'),
                height: getStyleVar('--theme-drag-drop-blank-section-default-height'),
                ...styleObj,
                minWidth: 'fit-content',
                minHeight: 'fit-content'
              }} title={stripTagsAndEntities(blankAccessibleText)}>
                <DndKitDraggable key={dataId}
                  activeStyle={{
                    background: getStyleVar('--theme-drag-drop-option-bg-color'),
                    border: `1px solid ${getStyleVar('--theme-drag-drop-option-border-color')}`,
                    padding: '12px',
                  // zIndex: 999 // TODO // 999 is the default for dnd-kit draggables, but we can lower this here (if needed)
                  }}
                  draggableId={dataId}
                  draggableInnerId={currentUserInputId}
                  draggableValue={blankDisplayHtml}
                  droppableId={dataId}
                  index={currentResponseItemIndex}
                  isDragDisabled={isDragDisabled}>
                  <div
                    className={classNames('cloze-drag-drop-prompt-option')}
                    style={{
                      minWidth: styleObj.width || getStyleVar('--theme-drag-drop-blank-section-default-width'),
                      minHeight: styleObj.height || getStyleVar('--theme-drag-drop-blank-section-default-height')
                    }}>
                    <div className={classNames('cloze-blank-droppable-inner-text',
                    `text-align-${model.answerAlignment || 'left'}`)} data-answer-id={currentUserInputId || null}>
                      <HtmlComponent htmlStr={stripWrappingParagraphTags(blankDisplayHtml)} useSpan />
                    </div>
                  </div>
                </DndKitDraggable>
              </div>
            </div>
          </DndKitDroppable>
        </div>
        <FeedbackIcon
          answerId={dataId}
          lessonElementId={lessonElementId}
          lessonElementState={lessonElementState}
          model={model}
          uiState={uiState}
          {...lessonElementState.currentResponseAnswer}
          {...textQuestionUtilsService.getFeedbackIconOptionsForDecoratingTextInputs()} />
      </div>
    );
  };
  return !loadingDragDrop && lessonElementId && model
    /* && typeof uiState?.submitButtonVisible === 'boolean' */ ? renderClozeDragDrop() : null;
});

export default ClozeDragDrop;
